// Client-side cooldown for auth-code send buttons (email + SMS).
//
// The backend already enforces hard rate limits (5/hr/phone, 20/hr/IP for
// SMS; silent 204 on overflow), but those give the user no feedback. This
// layer caps the UI at 1 send / 60s / identifier so the Send and Resend
// buttons can't race past the backend limits, matches the "Resend in ~60s"
// UX note in the integration spec, and prevents accidental double-clicks.
//
// Stored in localStorage (SSR-safe no-ops) keyed by identifier (phone in
// E.164 form, or email address), so closing and reopening the modal
// preserves the cooldown. Entries older than the cooldown window are
// lazily pruned on read — no background sweep needed.

export const COOLDOWN_MS = 60_000;

const STORAGE_KEY = "capcam.auth.cooldown.v1";

type CooldownMap = Record<string, number>;

const readMap = (): CooldownMap => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    return parsed as CooldownMap;
  } catch {
    return {};
  }
};

const writeMap = (map: CooldownMap): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // localStorage might be disabled (private mode / quota). Cooldown
    // just degrades to session-only — not a fatal error.
  }
};

/**
 * Milliseconds remaining before `identifier` can request another code.
 * 0 when no active cooldown. Returns 0 for empty identifiers so callers
 * can pass raw input without guarding.
 */
export const getCooldownRemaining = (
  identifier: string,
  now: number = Date.now()
): number => {
  if (!identifier) return 0;
  const map = readMap();
  const sentAt = map[identifier];
  if (typeof sentAt !== "number") return 0;
  const remaining = sentAt + COOLDOWN_MS - now;
  return remaining > 0 ? remaining : 0;
};

/**
 * Record that a code was just sent to `identifier`. Also prunes expired
 * entries so the map can't grow unbounded across a user's lifetime.
 */
export const markSent = (
  identifier: string,
  now: number = Date.now()
): void => {
  if (!identifier) return;
  const map = readMap();
  const pruned: CooldownMap = {};
  for (const [key, ts] of Object.entries(map)) {
    if (typeof ts === "number" && ts + COOLDOWN_MS > now) {
      pruned[key] = ts;
    }
  }
  pruned[identifier] = now;
  writeMap(pruned);
};

/**
 * Test helper — wipes the cooldown map. Not exported from the index; import
 * directly from this module in tests.
 */
export const __clearCooldownForTests = (): void => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(STORAGE_KEY);
};
